import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useConversation } from '@elevenlabs/react';
import {
  Phone,
  X,
  Car,
  Mic,
  MicOff,
  Volume2,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { ELEVENLABS_AGENT, DEFAULT_CONNECTION_TYPE } from '../elevenlabs/constants';
import { API_BASE_URL } from '../elevenlabs/apiBase';
import apiService from '../services/apiService';
import {
  normalizeWidgetAppointment,
  buildWidgetCallSummary,
  widgetCallStatus,
  WIDGET_APPT_STORAGE_KEY,
} from '../utils/widgetAppointment';

const AgentWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [sessionHint, setSessionHint] = useState('');
  const [volume, setVolume] = useState(1);
  const [textMessage, setTextMessage] = useState('');
  const [messages, setMessages] = useState([]);

  const conversationIdRef = useRef(null);
  const sessionStartRef = useRef(null);
  const lastAppointmentRef = useRef(null);
  const sessionLoggedRef = useRef(false);
  const messagesRef = useRef([]);

  const appendMessage = useCallback((source, text) => {
    setMessages((prev) => {
      const next = [...prev.slice(-49), { source, text, at: Date.now() }];
      messagesRef.current = next;
      return next;
    });
  }, []);

  // Tek noktadan tüm oturum verisini temizle
  const clearSessionData = useCallback(() => {
    lastAppointmentRef.current = null;
    try { sessionStorage.removeItem(WIDGET_APPT_STORAGE_KEY); } catch { /* ignore */ }
    if (typeof window !== 'undefined') window.__mersoConversationId = null;
  }, []);

  useEffect(() => {
    const onAppt = (e) => {
      lastAppointmentRef.current = normalizeWidgetAppointment(e.detail);
      appendMessage('system', 'Randevu kaydedildi — görüşme bitince çağrı listesine düşecek');
    };
    window.addEventListener('merso-appointment-booked', onAppt);
    return () => window.removeEventListener('merso-appointment-booked', onAppt);
  }, [appendMessage]);

  // Sunucudan conversationId ile randevu ara (son çare)
  const fetchAppointmentByConvId = useCallback(async (convId) => {
    if (!convId) return null;
    try {
      const list = await apiService.fetchAppointments();
      const match = list
        .filter((a) => a.conversationId === convId)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      return normalizeWidgetAppointment(match) || null;
    } catch {
      return null;
    }
  }, []);

  const logSessionEnd = useCallback(async () => {
    if (sessionLoggedRef.current || !sessionStartRef.current) return;
    sessionLoggedRef.current = true;

    // Tüm verileri async başlamadan ÖNCE snapshot'la
    const startedAt = sessionStartRef.current;
    const convId = conversationIdRef.current;
    const apptFromRef = lastAppointmentRef.current;
    const transcriptLines = messagesRef.current
      .filter((m) => m.source === 'user' || m.source === 'agent')
      .map((m) => `${m.source === 'user' ? 'Müşteri' : 'Asistan'}: ${m.text}`);

    // Snapshot alındıktan hemen sonra temizle — yeni oturum stale veri görmez
    clearSessionData();

    const durationSeconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
    const transcript = transcriptLines.join('\n');

    // Randevuyu bul: ref → sessionStorage (cleared above, use snapshot) → API
    let appt = normalizeWidgetAppointment(apptFromRef);
    if (!appt && convId) {
      appt = await fetchAppointmentByConvId(convId);
    }

    const summary =
      buildWidgetCallSummary(appt) ||
      transcript.slice(0, 400) ||
      'Web paneli sesli görüşme';

    try {
      await apiService.logWidgetSession({
        callTime: new Date(startedAt).toISOString(),
        durationSeconds,
        conversation_id: convId,
        appointment: appt,
        transcript,
        summary,
        call_status: widgetCallStatus(appt),
        phoneNumber: appt?.customerPhone,
      });
      appendMessage('system', 'Görüşme "Çağrı Kayıtları" ve özet panele eklendi');
    } catch (e) {
      appendMessage('system', `Kayıt hatası: ${e.message}`);
    }
  }, [appendMessage, clearSessionData, fetchAppointmentByConvId]);

  const conversation = useConversation({
    onConnect: ({ conversationId }) => {
      setError(null);
      // Yeni oturum başlıyor — ESKİ veriyi temizle
      clearSessionData();
      sessionLoggedRef.current = false;
      sessionStartRef.current = Date.now();
      messagesRef.current = [];
      setMessages([]);

      const cid = conversationId || null;
      conversationIdRef.current = cid;
      if (typeof window !== 'undefined') window.__mersoConversationId = cid;
      appendMessage('system', 'Bağlantı kuruldu');
    },
    onDisconnect: async () => {
      await logSessionEnd();
      appendMessage('system', 'Bağlantı sonlandı');
      conversationIdRef.current = null;
      sessionStartRef.current = null;
    },
    onStatusChange: ({ status }) => setSessionHint(status),
    onMessage: (message) => {
      const text = message?.message || message?.text;
      if (text) {
        const source =
          message.source === 'user' || message.role === 'user' ? 'user' : 'agent';
        appendMessage(source, text);
      }
    },
    onError: (message, context) => {
      const msg =
        (typeof message === 'string' ? message : message?.message) ||
        'Görüşme hatası';
      setError(msg);
      appendMessage('system', `Hata: ${msg}`);
      if (context?.errorType) {
        console.warn('[AgentWidget] ElevenLabs error:', context);
      }
    },
    onUnhandledClientToolCall: (tool) => {
      appendMessage('system', `Tanımsız araç: ${tool?.toolName || '?'}`);
    },
  });

  const buildSessionOptions = async () => {
    const res = await fetch(`${API_BASE_URL}/elevenlabs/conversation-token`);
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success && json.token) {
      return { connectionType: DEFAULT_CONNECTION_TYPE, conversationToken: json.token };
    }
    throw new Error(json.error || `Oturum token alınamadı (${res.status})`);
  };

  const startConversation = async () => {
    setError(null);
    setSessionHint('connecting');
    if (conversation.status === 'connected') return;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      conversation.startSession(await buildSessionOptions());
      appendMessage('system', 'Görüşme başlatılıyor…');
    } catch (err) {
      setError(err.message || 'Mikrofon erişimi reddedildi');
      setSessionHint('disconnected');
    }
  };

  const stopConversation = () => {
    conversation.endSession();
    setSessionHint('disconnected');
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (conversation.status === 'connected') {
      conversation.setVolume({ volume: v });
    }
  };

  const sendText = () => {
    if (!textMessage.trim() || conversation.status !== 'connected') return;
    conversation.sendUserMessage(textMessage.trim());
    appendMessage('user', textMessage.trim());
    setTextMessage('');
  };

  const isConnected = conversation.status === 'connected';
  const isConnecting = conversation.status === 'connecting' || sessionHint === 'connecting';

  const statusLabel = {
    connected: 'Bağlı',
    connecting: 'Bağlanıyor…',
    disconnected: 'Kapalı',
  }[conversation.status] || conversation.status;

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="bg-accent-gradient text-white rounded-full p-4 shadow-glow-cyan ring-1 ring-white/10 transition-all duration-300 hover:scale-110 hover:shadow-glow-cyan-lg group"
          title="Dijital randevu asistanı"
        >
          <div className="flex items-center gap-2">
            <Car className="w-6 h-6" />
            <Phone className="w-5 h-5" />
          </div>
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-cockpit-800 text-white text-sm rounded-lg ring-1 ring-white/10 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {ELEVENLABS_AGENT.shortName}
          </div>
        </button>
      </div>

      {isOpen && (
        <div className="glass-overlay fixed inset-0 flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl w-full max-w-lg h-[min(720px,90vh)] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-gradient shadow-glow-cyan">
                  <Car className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{ELEVENLABS_AGENT.shortName}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Mengerler Yenimahalle</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (isConnected) stopConversation();
                  setIsOpen(false);
                  setMessages([]);
                  messagesRef.current = [];
                  setError(null);
                }}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 flex flex-col p-4 min-h-0 gap-4">
              <div className="flex items-center justify-between bg-white/5 ring-1 ring-white/10 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-white">Durum: {statusLabel}</p>
                  <p className="text-xs text-slate-400">
                    {isConnected
                      ? conversation.isSpeaking
                        ? 'Asistan konuşuyor'
                        : 'Dinliyor'
                      : 'Mikrofon gerekli'}
                  </p>
                </div>
                {isConnected && (
                  <span
                    className={`inline-flex h-3 w-3 rounded-full ${
                      conversation.isSpeaking
                        ? 'bg-accent-400 animate-pulse shadow-[0_0_10px_rgba(34,211,238,0.9)]'
                        : 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]'
                    }`}
                  />
                )}
              </div>

              {error && (
                <div className="text-sm text-rose-200 bg-rose-500/10 border border-rose-400/30 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex gap-2">
                {!isConnected ? (
                  <button
                    type="button"
                    onClick={startConversation}
                    disabled={isConnecting}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    {isConnecting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mic className="w-4 h-4" />
                    )}
                    Başlat
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={stopConversation}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <MicOff className="w-4 h-4" />
                    Durdur
                  </button>
                )}
              </div>

              {isConnected && (
                <>
                  <label className="flex items-center gap-2 text-sm text-slate-400">
                    <Volume2 className="w-4 h-4 text-accent-300" />
                    Ses
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="flex-1 accent-accent-400"
                    />
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={textMessage}
                      onChange={(e) => setTextMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && sendText()}
                      placeholder="Yazılı mesaj gönder…"
                      className="field flex-1"
                    />
                    <button type="button" onClick={sendText} className="btn-secondary px-3">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto rounded-lg bg-black/20 ring-1 ring-white/10 p-3 space-y-2">
                {messages.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Başlat&apos;a basın. Görüşme bitince kayıt Çağrı Kayıtları&apos;nda görünür.
                  </p>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.at}
                      className={`text-xs rounded-lg px-2.5 py-1.5 ${
                        m.source === 'user'
                          ? 'bg-accent-400/15 text-accent-100 ring-1 ring-accent-400/20 ml-4'
                          : m.source === 'system'
                            ? 'bg-white/5 text-slate-400'
                            : 'bg-white/[0.06] text-slate-200 mr-4 ring-1 ring-white/10'
                      }`}
                    >
                      {m.text}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentWidget;
